import { useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import CartSelectionClosedPhase from "./cart-selection-closed-phase";
import CartSelectionPhase from "./cart-selection-phase";
import PickUpPointBookingPhase from "./pick-up-point-booking-phase";
import CartDetail from "./cart-detail";
import Title from "../title";
import useCartRequestCreateMutation from "../../hooks/mutations/useCartRequestCreateMutation.js";
import useCartRequestUpdateMutation from "../../hooks/mutations/useCartRequestUpdateMutation";
import CartContext from "../../context/cart";
import UserContext from "../../context/user";
import { getAnswerValue, parseSubmission } from "../../utils/partial";
import CartClosedCampaign from "./cart-closed-campaign";
import AppError from "../app-error";

const CarrelloBody = () => {
  const { logged, callApi } = useContext(UserContext);
  const {
    labels,
    campaign,
    survey,
    status,
    showDetail,
    setShowDetail,
    setPartial,
    setFormData,
    cartRequest,
    setCartRequest,
    setStatus,
    currentSectionIndex,
    setCurrentSectionIndex,
  } = useContext(CartContext);
  const getSectionIndexFromRequest = (request) => {
    return (
      {
        "cart-selection": getAnswerValue(survey, request?.surveySubmission?.answers, 1, 0)
          ?.answerValue
          ? 2
          : 1,
        "slot-booking":
          {
            NONE: request?.status === "CART_NOT_SELECTED" ? -5 : -3,
            SEDE: request?.contentId ? -1 : -5,
            PUNTO_VENDITA:
              !request.defaultCartAssigned && request.status === "CART_NOT_SELECTED"
                ? -5
                : request?.status === "DELIVERED"
                ? -2
                : request?.status === "BOOKED"
                ? 4
                : request?.reservationId
                ? 3
                : getAnswerValue(survey, request?.surveySubmission?.answers, 4, 0)?.answerValue
                ? 2
                : getAnswerValue(survey, request?.surveySubmission?.answers, 3, 2)?.answerValue
                ? 1
                : 0,
            INDIRIZZO: request?.contentId ? -4 : -5,
            // request?.status === "BOOKED"
            //   ? 3
            //   : request?.deliveryInformation?.id
            //   ? 2
            //   : getAnswerValue(survey, request?.surveySubmission?.answers, 3, 2)?.answerValue
            //   ? 1
            //   : 0,
          }[request?.deliveryOption] || 0,
      }[status] || 0
    );
  };

  const [tempCartId, setTempCartId] = useState(undefined);

  const { isFetching, error } = useQuery(["cart-requests"], () => callApi("/cart/request/search"), {
    enabled: logged && !!status && status !== "invalid-campaign",
    select: (data) => data?.list,
    onSuccess: (data) => {
      if (data.length === 0) {
        setCurrentSectionIndex(0);
        if (status !== "cart-selection") {
          setStatus("closed");
        }
      } else {
        setCurrentSectionIndex(getSectionIndexFromRequest(data[0]));
        setCartRequest(data[0]);
        setPartial(data[0]?.surveySubmission?.submissionId);
        const cartQuestionId = survey?.definition?.sections?.[0]?.id;
        setFormData({
          ...parseSubmission(survey, data[0]?.surveySubmission),
          [cartQuestionId]: data[0].contentId,
        });
      }
    },
  });

  const { mutateAsync: createCartRequest, ...cartRequestMutation } = useCartRequestCreateMutation();

  const { mutateAsync: updateCartRequest, isLoading: updateIsLoading } =
    useCartRequestUpdateMutation();

  const handleCartSelection = async (e) => {
    // evita di spedire un'altra request verso tamtami se l'utente preme velocemente più di una volta
    if (updateIsLoading || cartRequestMutation.isLoading) {
      return;
    }

    const id = e?.currentTarget?.id?.split("-")?.[0];

    // se il carrello è già stato scelto non manda subito l'aggiornamento, viene fatto allo step successivo
    // in questo modo l'utente torna al riassunto del carrello scelto in precedenza se non finisce l'iter o refresha
    if (cartRequest?.status === "CREATED") {
      setTempCartId(Number(id));
      setCurrentSectionIndex((current) => current + 1);
      setShowDetail(false);
      return;
    }

    if (id) {
      if (cartRequest?.contentId) {
        if (cartRequest?.contentId !== Number(id)) {
          await updateCartRequest({
            campaignId: campaign?.id,
            cartRequest: { ...cartRequest, contentId: Number(id) },
            survey,
            formData: {},
            values: {},
          });
        } else {
          setCurrentSectionIndex((current) => current + 1);
        }
      } else {
        await createCartRequest({ survey, campaignId: campaign?.id, cartId: id });
      }
      setShowDetail(false);
    }
  };

  function cartSelectionClosedLabel() {
    if (!cartRequest?.defaultCartAssigned && cartRequest?.status === "CART_NOT_SELECTED") {
      return labels?.WELFARE_CART_CLOSED_TITLE;
    }

    if (cartRequest?.deliveryOption === "INDIRIZZO") {
      return labels?.WELFARE_CART_HOME_DELIVERY_TITLE || "Shipping Cart";
    }
    if (cartRequest?.defaultCartAssigned || cartRequest?.status === "CART_NOT_SELECTED") {
      return (
        labels?.WELFARE_CART_SELECTION_PHASE_CLOSED_TITLE || "The cart selection phase is closed"
      );
    }
    return (
      labels?.WELFARE_CART_SELECTION_COMPLETED_TITLE || "Thank you for completing the procedure"
    );
  }

  return error ? (
    <AppError labels={labels} />
  ) : isFetching ? (
    <div className="loading-spinner mt" />
  ) : (
    <>
      {!showDetail && (
        <Title
          showStepper={status === "slot-booking" ? currentSectionIndex >= 0 : true}
          stepper={{
            sections: {
              "cart-selection": [0, 1, 2],
              "slot-booking":
                cartRequest?.deliveryOption === "INDIRIZZO" ? [0, 1, 2, 3] : [0, 1, 2, 3, 4],
            }[status],
            currentSectionIndex: currentSectionIndex,
          }}
          description={
            {
              "slot-booking":
                cartRequest?.deliveryOption === "INDIRIZZO" &&
                currentSectionIndex === 0 &&
                (labels?.WELFARE_CART_HOME_DELIVERY_DESCRIPTION ||
                  "The shopping cart you have chosen in recent months is in preparation, at this stage you can tell us the address where you want to receive it"),
            }[status]
          }
        >
          {
            {
              "cart-selection": labels?.WELFARE_CART_SELECTION_TITLE || "Cart selection",
              "slot-booking":
                {
                  NONE:
                    cartRequest?.status === "CART_NOT_SELECTED"
                      ? labels?.WELFARE_CART_SELECTION_PHASE_CLOSED_TITLE ??
                        "The cart selection phase is closed"
                      : labels?.WELFARE_CART_PICKUP_FALLBACK_TITLE || "Cart pickup",
                  INDIRIZZO:
                    currentSectionIndex === -5
                      ? labels?.WELFARE_CART_CLOSED_TITLE
                      : labels?.WELFARE_CART_HOME_DELIVERY_TITLE || "Shipping Cart",
                  PUNTO_VENDITA: cartRequest?.defaultCartAssigned
                    ? labels?.WELFARE_PICKUP_POINT_BOOKING_TITLE
                    : currentSectionIndex === -5
                    ? labels?.WELFARE_CART_CLOSED_TITLE
                    : currentSectionIndex === -2
                    ? labels?.WELFARE_CART_DELIVERED_TITLE || "Your cart has been delivered"
                    : labels?.WELFARE_PICKUP_POINT_BOOKING_TITLE || "Pick-up point booking",
                  SEDE:
                    currentSectionIndex === -5
                      ? labels?.WELFARE_CART_CLOSED_TITLE
                      : labels?.WELFARE_PICKUP_POINT_SEDE_TITLE ||
                        "The cart will be delivered to your workplace",
                }[cartRequest?.deliveryOption] || 0,
              "cart-selection-closed": cartSelectionClosedLabel(),
              closed: labels?.WELFARE_CART_CLOSED_TITLE || "Shopping cart initiative is closed",
              "invalid-campaign":
                labels?.WELFARE_CART_CLOSED_TITLE || "Shopping cart initiative is closed",
            }[status]
          }
        </Title>
      )}
      {showDetail ? (
        <CartDetail handleCartSelection={handleCartSelection} />
      ) : (
        {
          "cart-selection": (
            <CartSelectionPhase
              cartRequestMutation={cartRequestMutation}
              handleCartSelection={handleCartSelection}
              tempCartId={tempCartId}
              setTempCartId={setTempCartId}
            />
          ),
          "cart-selection-closed": <CartSelectionClosedPhase />,
          "slot-booking": <PickUpPointBookingPhase />,
          closed: <CartClosedCampaign />,
          "invalid-campaign": <CartClosedCampaign />,
        }[status]
      )}
    </>
  );
};

export default CarrelloBody;
